import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContentLanguage } from '@/utils/contentLanguage';

import {
    AiPromptBlockedError,
    AiPromptModal,
    AiPromptStepError,
    type AiPromptIntegrationMode,
    type AiPromptModalConfig,
    type AiPromptModalRef,
} from '@/components/AiPromptModal/AiPromptModal';
import {
    canAddGeneratedAgenda,
    type GeneratedAgenda,
} from '@/components/MeetingAgendaBuilder/agendaViewModel';
import { GeneratedAgendaReview } from '@/components/MeetingAgendaBuilder/components/GeneratedAgendaReview';
import { SensitiveObjectiveRefusal } from '@/components/MeetingAgendaBuilder/sensitiveObjectiveRefusal';

export type AgendaIntegrationMode = AiPromptIntegrationMode;
export type AgendaPromptModalRef = AiPromptModalRef;

interface AgendaPromptModalProps {
    /**
     * Runs AI agenda generation with the manager's objective when `askObjective` is set, and the context when
     * `askContext` is set (an empty string for what is not asked), resolving to the parsed agenda or null on
     * failure/timeout. The modal advances to the integration step only on a non-null result. A
     * {@link SensitiveObjectiveRefusal} shows the objective's sentences under the objective, which must be edited
     * before generating again, and names each refused noted topic apart: those are edited in the meeting
     * preparation, so Generate stays disabled until the modal is opened again.
     */
    onGenerate: (objective: string, context: string) => Promise<GeneratedAgenda | null>;
    /** Integrates the generated agenda: 'add' appends it to the current items, 'replace' overwrites them. */
    onIntegrate: (mode: AgendaIntegrationMode, generated: GeneratedAgenda) => void;
    /** When false, "Add to agenda" is disabled — there are no existing items to append to. */
    canAdd?: boolean;
    /**
     * The language the current agenda is written in. A generated plan in another language cannot be added
     * to it — the agenda would mix two — so "Add to agenda" is disabled with a message pointing to Replace.
     */
    agendaLanguage?: ContentLanguage;
    /**
     * Whether the modal asks for the private context (after the objective, when one is asked). The post-review
     * plan no longer does (it is never sent to the model); the thematic meeting's plan still does.
     */
    askContext?: boolean;
    /**
     * Whether the modal asks for an objective first. The thematic meeting's plan no longer does: it is adapted to the
     * context alone, so the modal asks only for that.
     */
    askObjective?: boolean;
    dataTestId?: string;
}

const OBJECTIVE_STEP = 0;

const AGENDA_CONFIG: AiPromptModalConfig = {
    headerTitleKey: 'meetingAgenda.aiAgenda.title',
    imageAltKey: 'meetingAgenda.aiAgenda.imageAlt',
    overlayGeneratingKey: 'meetingAgenda.aiAgenda.overlay.generating',
    overlayMessageKey: 'meetingAgenda.aiAgenda.overlay.message',
    generateButtonKey: 'meetingAgenda.aiAgenda.step2.generateButton',
    steps: [
        {
            iconName: 'essential-flash',
            questionKey: 'meetingAgenda.aiAgenda.step1.question',
            placeholderKey: 'meetingAgenda.aiAgenda.step1.placeholder',
            rows: 6,
        },
        {
            iconName: 'files-folder',
            questionKey: 'meetingAgenda.aiAgenda.step2.question',
            placeholderKey: 'meetingAgenda.aiAgenda.step2.placeholder',
            rows: 5,
            optional: true,
        },
    ],
    integration: {
        titleKey: 'meetingAgenda.aiAgenda.integrate.title',
        add: {
            titleKey: 'meetingAgenda.aiAgenda.integrate.addTitle',
            descriptionKey: 'meetingAgenda.aiAgenda.integrate.addDescription',
        },
        replace: {
            titleKey: 'meetingAgenda.aiAgenda.integrate.replaceTitle',
            descriptionKey: 'meetingAgenda.aiAgenda.integrate.replaceDescription',
        },
        replaceConfirm: {
            titleKey: 'meetingAgenda.aiAgenda.replaceConfirm.title',
            messageKey: 'meetingAgenda.aiAgenda.replaceConfirm.message',
            questionKey: 'meetingAgenda.aiAgenda.replaceConfirm.question',
            confirmKey: 'meetingAgenda.aiAgenda.replaceConfirm.confirm',
            cancelKey: 'meetingAgenda.aiAgenda.replaceConfirm.cancel',
        },
    },
};

/** The same modal asking for the objective only. */
const OBJECTIVE_ONLY_CONFIG: AiPromptModalConfig = {
    ...AGENDA_CONFIG,
    steps: AGENDA_CONFIG.steps.slice(0, 1),
};

/** The same modal asking for the context only. */
const CONTEXT_ONLY_CONFIG: AiPromptModalConfig = {
    ...AGENDA_CONFIG,
    steps: AGENDA_CONFIG.steps.slice(1),
};

/** The modal's questions: the objective and the context, each when asked for. */
const configFor = (askObjective: boolean, askContext: boolean): AiPromptModalConfig => {
    if (!askObjective) return CONTEXT_ONLY_CONFIG;
    return askContext ? AGENDA_CONFIG : OBJECTIVE_ONLY_CONFIG;
};

/**
 * The "Generate a Meeting Plan with Amélio AI" modal — the same multi-step AI modal as recommendations,
 * configured with the agenda's questions (the objective and the context, each when asked for) and an Add / Replace
 * integration step (Replace shows a confirmation, since it discards the current plan). The integration step first
 * shows the summary and the alerts the plan comes with, when it comes with any.
 */
export const AgendaPromptModal = forwardRef<AgendaPromptModalRef, AgendaPromptModalProps>(
    (
        {
            onGenerate,
            onIntegrate,
            canAdd,
            agendaLanguage,
            askContext = true,
            askObjective = true,
            dataTestId = 'agenda-prompt-modal',
        },
        ref,
    ) => {
        const { t } = useTranslation();

        const generate = async (answers: string[]) => {
            if (!askObjective) {
                return onGenerate('', answers[0]);
            }
            try {
                return await onGenerate(answers[OBJECTIVE_STEP], askContext ? answers[1] : '');
            } catch (error) {
                if (error instanceof SensitiveObjectiveRefusal) {
                    throw refusalFor(error);
                }
                throw error;
            }
        };

        // The objective's sentences go under the objective; a refused noted topic blocks Generate with its title,
        // since no edit of the objective would lift it.
        const refusalFor = (refusal: SensitiveObjectiveRefusal) => {
            const objectiveError =
                refusal.objectiveSentences.length > 0
                    ? new AiPromptStepError(
                          OBJECTIVE_STEP,
                          t('meetingAgenda.aiAgenda.sensitiveObjective.message'),
                          refusal.objectiveSentences,
                      )
                    : null;
            if (refusal.topics.length === 0) {
                return (
                    objectiveError ??
                    new AiPromptStepError(
                        OBJECTIVE_STEP,
                        t('meetingAgenda.aiAgenda.sensitiveObjective.message'),
                    )
                );
            }
            return new AiPromptBlockedError(
                t('meetingAgenda.aiAgenda.sensitiveTopic.message'),
                refusal.topics.map(({ title }) =>
                    t('meetingAgenda.aiAgenda.sensitiveTopic.item', { title }),
                ),
                objectiveError,
            );
        };

        const addBlockedReason = (generated: GeneratedAgenda) => {
            if (!agendaLanguage || canAddGeneratedAgenda({ language: agendaLanguage }, generated)) {
                return null;
            }
            return t(
                generated.language === 'fr'
                    ? 'meetingAgenda.aiAgenda.integrate.languageMismatch.toFrench'
                    : 'meetingAgenda.aiAgenda.integrate.languageMismatch.toEnglish',
            );
        };

        return (
            <AiPromptModal<GeneratedAgenda>
                ref={ref}
                config={configFor(askObjective, askContext)}
                onGenerate={generate}
                onIntegrate={onIntegrate}
                canAdd={canAdd}
                addBlockedReason={addBlockedReason}
                renderResultReview={(generated) => (
                    <GeneratedAgendaReview
                        title={t('meetingAgenda.aiAgenda.review.summaryTitle')}
                        summary={generated.summary}
                        alerts={generated.alerts}
                        dataTestId={`${dataTestId}-review`}
                    />
                )}
                dataTestId={dataTestId}
            />
        );
    },
);

AgendaPromptModal.displayName = 'AgendaPromptModal';
